import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound,
  Lock,
  PencilLine,
  Plus,
  ShieldCheck,
  ShieldHalf,
  Trash2,
  UserPlus,
  UserX,
  Users,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { HeroChip } from '@/components/ui/HeroChip';
import { Skeleton } from '@/components/ui/Skeleton';
import { ALL_PERMISSIONS, ROLE_KIND_LABEL } from '@/constants/permissions';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { collectPages, EXPORT_PAGE_SIZE } from '@/lib/collectPages';
import { cn } from '@/lib/utils';
import type { InstituteRole } from '@/types/role';
import type { StaffMember } from '@/types/staff';
import { instituteApi } from '../../api/institute.api';
import { staffApi } from '../../staff/api/staff.api';
import { StaffFormModal } from '../../staff/components/StaffFormModal';
import { setStaffRole } from '../../staff/staffStatus';
import { AccessGraphic } from '../components/AccessGraphic';
import { RoleAccessMeter, coverageOf } from '../components/RoleAccessMeter';
import { RoleHolders } from '../components/RoleHolders';
import { QueryError } from '@/components/ui/QueryError';

/**
 * The roles an institute has defined, and what each one allows.
 *
 * Read-only for anyone holding ROLE_VIEW without ROLE_MANAGE — seeing how
 * access is arranged is useful even to someone who may not change it, and the
 * server enforces the same split.
 */
export function RolesPage() {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('ROLE_MANAGE'));
  // Opening an account is the staff permission, not the roles one.
  const canAddStaff = Boolean(permissions?.includes('STAFF_MANAGE'));
  // Who holds each role is the staff list, so it needs that permission to read.
  const canViewStaff = Boolean(permissions?.includes('STAFF_VIEW'));
  const selfUuid = useAuthStore((state) => state.user?.uuid);
  const schoolFeatures = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);

  const {
    data: roles,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => instituteApi.listRoles(), []);

  const [deleting, setDeleting] = useState<InstituteRole | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /** The role a new member of staff is being opened for, from its card. */
  const [addingFor, setAddingFor] = useState<InstituteRole | null>(null);
  /** A holder about to be moved, waiting on the confirmation. */
  const [moving, setMoving] = useState<{ member: StaffMember; to: InstituteRole } | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  /*
    Everybody on staff, once, grouped by role below — one walk of the list
    rather than a request per card. Every page, so a large school's holders
    are not cut off at the first hundred.
  */
  const staff = useApiQuery(
    () =>
      canViewStaff
        ? collectPages((page) => staffApi.list({ page, limit: EXPORT_PAGE_SIZE }))
        : Promise.resolve(null),
    [canViewStaff],
  );
  const holdersByRole = useMemo(() => {
    const groups = new Map<string, StaffMember[]>();
    for (const member of staff.data?.items ?? []) {
      if (!member.role) continue;
      const group = groups.get(member.role.uuid) ?? [];
      group.push(member);
      groups.set(member.role.uuid, group);
    }
    return groups;
  }, [staff.data]);
  // The Administrator role is the owner's alone, so nobody is moved onto it.
  const moveOptions = useMemo(
    () => (roles ?? []).filter((role) => !role.isSystem).map((role) => ({ value: role.uuid, label: role.name })),
    [roles],
  );

  function requestMove(member: StaffMember, roleUuid: string) {
    const to = roles?.find((role) => role.uuid === roleUuid);
    if (to) setMoving({ member, to });
  }

  async function confirmMove() {
    if (!moving) return;
    const { member, to } = moving;
    setIsMoving(true);
    try {
      const outcome = await setStaffRole(member, to.uuid);
      setMoving(null);
      if (!outcome.ok) {
        toast.error(
          `${member.fullName}'s record needs correcting first`,
          'Open their record on the Staff page, fix the highlighted details and save, then change the role.',
        );
        return;
      }
      toast.success(`${outcome.saved.fullName} now has the ${to.name} role`, 'Their access changes from their next page load.');
      refetch();
      staff.refetch();
    } catch (caught) {
      setMoving(null);
      toast.error(
        'Could not change the role',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsMoving(false);
    }
  }

  function handleStaffSaved(saved: StaffMember) {
    setAddingFor(null);
    toast.success(
      `${saved.fullName} has been added`,
      `An email is on its way to ${saved.email} with a link to set their password.`,
    );
    // The card's "people hold this role" count has just changed.
    refetch();
    staff.refetch();
  }

  async function confirmDelete() {
    if (!deleting) return;
    setIsDeleting(true);
    setActionError(null);
    try {
      await instituteApi.deleteRole(deleting.uuid);
      setDeleting(null);
      refetch();
    } catch (caught) {
      setActionError(
        caught instanceof ApiRequestError ? caught.message : 'That role could not be deleted.',
      );
      setDeleting(null);
    } finally {
      setIsDeleting(false);
    }
  }

  /**
   * What the access model adds up to.
   *
   * All four are counted off the roles themselves — nothing here is a figure
   * the screen invented. "Unused" is the one worth reading: a role nobody holds
   * is either a job that never happened or one somebody forgot to assign.
   */
  const totalPermissions = ALL_PERMISSIONS.length;
  const assigned = roles?.reduce((sum, role) => sum + role.userCount, 0) ?? 0;
  const unused = roles?.filter((role) => role.userCount === 0).length ?? 0;
  const granted = new Set(roles?.flatMap((role) => role.permissions) ?? []).size;

  if (addingFor) {
    return (
      <StaffFormModal
        member={null}
        isOpen
        roles={roles ?? []}
        initialRole={addingFor}
        onClose={() => setAddingFor(null)}
        onSaved={handleStaffSaved}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE)}>
      {/* The banner: what access looks like here, over a blue wash. */}
      <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-[#172554]/50 p-3.5 sm:py-3.5 sm:px-5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
        {/* Two lights drifting out of step behind the wash, and a single pass of
            light across it. All decoration, all held still under
            prefers-reduced-motion. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/30 to-indigo-500/20 blur-3xl animate-orb"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-28 right-1/4 h-56 w-56 rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl animate-orb-slow"
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen dark:via-white/10" />
        </div>

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3.5 sm:gap-4">
            <span
              className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25 sm:h-14 sm:w-14"
              aria-hidden
            >
              <ShieldCheck className="relative h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.9} />
              <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
            </span>

            <div className="min-w-0">
              <span className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                Access
              </span>
              <h1 className="mt-0.5 text-xl font-black tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                Roles
              </h1>
              <p className="mt-1 max-w-xl text-xs font-medium text-slate-600 dark:text-slate-300 sm:text-sm">
                A role decides what someone at your institute may do. Give each member of staff the
                one that matches their job.
              </p>

              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {!isLoading && roles && (
                  <>
                    <HeroChip icon={ShieldHalf}>
                      {roles.length} {roles.length === 1 ? 'role' : 'roles'}
                    </HeroChip>
                    <HeroChip icon={Users}>
                      {assigned} {assigned === 1 ? 'person assigned' : 'people assigned'}
                    </HeroChip>
                    <HeroChip icon={KeyRound}>
                      {granted} of {totalPermissions} permissions in use
                    </HeroChip>
                    {unused > 0 && (
                      <HeroChip icon={UserX}>
                        {unused} {unused === 1 ? 'role nobody holds' : 'roles nobody holds'}
                      </HeroChip>
                    )}
                  </>
                )}

                {canManage && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/roles/new')}
                    className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                  >
                    <Plus className="h-3.5 w-3.5 stroke-[2.5]" aria-hidden />
                    <span>New role</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="hidden w-[130px] shrink-0 lg:block xl:w-[150px]">
            <AccessGraphic />
          </div>
        </div>
      </div>

      {!canManage && (
        <Alert tone="info" title="Read only">
          Your role lets you see how access is arranged but not change it. Ask your institute
          administrator if something needs adjusting.
        </Alert>
      )}

      {actionError && (
        <Alert tone="critical" title="Could not delete that role">
          {actionError}
        </Alert>
      )}

      {error ? (
        <QueryError title="Could not load your roles" error={error} onRetry={refetch} />
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-52 w-full rounded-3xl" />
          ))}
        </div>
      ) : roles && roles.length === 0 ? (
        <div className="rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] sm:p-5">
          <EmptyState
            icon={<ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
            title="No roles yet"
            description="Create your first role — most institutes start with Principal, Teacher and Accountant."
            action={
              canManage ? (
                <button
                  type="button"
                  onClick={() => navigate('/admin/roles/new')}
                  className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  <Plus className="h-4 w-4 stroke-[2.5]" />
                  <span>New role</span>
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 animate-slide-down delay-75">
          {roles?.map((role, index) => {
            const coverage = coverageOf(role.permissions, schoolFeatures, featureNames);
            const reached = coverage.filter((group) => group.held > 0);
            const isUnused = role.userCount === 0;

            return (
              <section
                key={role.uuid}
                style={{ animationDelay: `${Math.min(index * 70, 420)}ms` }}
                className="group relative flex animate-card-pop flex-col gap-4 overflow-hidden rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] transition-all hover:-translate-y-0.5 hover:shadow-lg sm:p-5"
              >
                {/* The edge marks the roles that came with the institute — the
                    ones an administrator cannot delete however hard they try. */}
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-y-0 left-0 w-1',
                    role.isSystem
                      ? 'bg-gradient-to-b from-blue-500 to-indigo-600'
                      : 'bg-gradient-to-b from-sky-300 to-blue-400 dark:from-sky-700 dark:to-blue-600',
                  )}
                />

                <header className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span
                      className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25"
                      aria-hidden
                    >
                      <ShieldCheck className="relative h-5 w-5" />
                      <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
                    </span>

                    <div className="min-w-0">
                      <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] font-black tracking-tight text-slate-900 dark:text-white">
                        <span className="truncate">{role.name}</span>
                        {role.isSystem && (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-blue-700 dark:text-blue-300"
                            title="Came with your institute — cannot be deleted"
                          >
                            <Lock className="h-2.5 w-2.5" aria-hidden />
                            Locked
                          </span>
                        )}
                      </h2>
                      <p className="mt-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                        For {ROLE_KIND_LABEL[role.kind].toLowerCase()} staff
                      </p>
                      {role.description && (
                        <p className="mt-0.5 line-clamp-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {(canManage || canAddStaff) && (
                    <div className="flex shrink-0 flex-wrap justify-end gap-1">
                      {/* The Administrator role is the school's own account,
                          and the server refuses it for staff. */}
                      {canAddStaff && !role.isSystem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Add staff with the ${role.name} role`}
                          leftIcon={<UserPlus className="h-3.5 w-3.5" />}
                          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setAddingFor(role); }}
                        >
                          Add staff
                        </Button>
                      )}
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${role.name}`}
                          leftIcon={<PencilLine className="h-3.5 w-3.5" />}
                          onClick={() => navigate(`/admin/roles/${role.uuid}/edit`)}
                        >
                          Edit
                        </Button>
                      )}
                      {canManage && !role.isSystem && (
                        <Button
                          variant="dangerSoft"
                          size="sm"
                          aria-label={`Suspend ${role.name}`}
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          onClick={() => setDeleting(role)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </header>

                <RoleAccessMeter
                  coverage={coverage}
                  held={role.permissions.length}
                  total={totalPermissions}
                />

                {/*
                  The areas this role reaches, not every permission it holds.
                  Thirty identical badges is a wall nobody reads; nine areas with
                  a fraction against each is the same fact in a shape somebody
                  can compare against the role beside it. The full list is one
                  click away, in the form the Edit button opens.
                */}
                <div className="flex flex-wrap gap-1.5">
                  {reached.length === 0 && (
                    <p className="text-[12.5px] font-medium text-slate-400 dark:text-slate-500">
                      Allows nothing yet — this role opens no part of the product.
                    </p>
                  )}
                  {reached.map((group) => {
                    const isFull = group.held === group.total;

                    return (
                      <span
                        key={group.title}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold',
                          isFull
                            ? 'border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300'
                            : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-300',
                        )}
                      >
                        {group.title}
                        <span className="font-mono text-[10px] font-black tabular-nums opacity-70">
                          {group.held}/{group.total}
                        </span>
                      </span>
                    );
                  })}
                </div>

                {canViewStaff && (
                  <RoleHolders
                    roleUuid={role.uuid}
                    roleName={role.name}
                    holders={holdersByRole.get(role.uuid) ?? []}
                    roleOptions={moveOptions}
                    canChange={canAddStaff && !role.isSystem}
                    selfUuid={selfUuid}
                    savingUuid={isMoving ? (moving?.member.uuid ?? null) : null}
                    onMove={requestMove}
                  />
                )}

                <footer
                  className={cn(
                    'mt-auto flex items-center gap-1.5 border-t border-slate-200/80 dark:border-slate-800 pt-3 text-[11.5px] font-bold',
                    isUnused
                      ? 'text-amber-700 dark:text-amber-300'
                      : 'text-slate-500 dark:text-slate-400',
                  )}
                >
                  {isUnused ? (
                    <UserX className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  ) : (
                    <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  {isUnused
                    ? 'Nobody holds this role yet'
                    : `${role.userCount} ${role.userCount === 1 ? 'person holds' : 'people hold'} this role`}
                </footer>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(moving)}
        title={`Give ${moving?.member.fullName ?? 'them'} the ${moving?.to.name ?? 'new'} role?`}
        description={`They lose what ${moving?.member.role?.name ?? 'their current role'} allows and get what ${moving?.to.name ?? 'the new role'} allows instead.`}
        confirmLabel="Change role"
        isLoading={isMoving}
        onConfirm={confirmMove}
        onCancel={() => setMoving(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        title={`Suspend ${deleting?.name ?? 'this role'}?`}
        description={
          deleting?.userCount
            ? `${deleting.userCount} ${deleting.userCount === 1 ? 'person is' : 'people are'} using this role. Move them to another role first — a role cannot be suspended while it is in use.`
            : 'Nothing is deleted. The role stops being offered and can be restored at any time. Nobody is using it, so nobody loses access.'
        }
        confirmLabel="Suspend role"
        tone="danger"
        isLoading={isDeleting}
        isConfirmDisabled={Boolean(deleting?.userCount)}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
